import { createFileRoute } from "@tanstack/react-router";
import { History, Plus, ScrollText } from "lucide-react";
import { useEffect } from "react";
import { z } from "zod";
import { PageHeader } from "@/components/page-header";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { Note } from "@/components/ui/note";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useChangelog } from "@/hooks/use-changelog";
import { BRAND_NAME } from "@/lib/brand";
import { VERSION } from "@/lib/build-info";
import { parseInlineNote } from "@/lib/changelog";
import { releases } from "@/lib/releases";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_app/changelog")({
  validateSearch: z.object({ version: z.string().optional() }),
  component: ChangelogPage,
});

// The changelog's dates are calendar days, not instants: formatting them in
// UTC keeps "2026-10-12" from becoming Oct 11 west of Greenwich.
const releaseDate = new Intl.DateTimeFormat("en-US", { dateStyle: "medium", timeZone: "UTC" });

function ChangelogPage() {
  const { version } = Route.useSearch();
  const navigate = Route.useNavigate();
  const visibleReleases = version
    ? releases.filter((entry) => entry.version === version)
    : releases;

  return (
    <div className="mx-auto w-full max-w-[880px]">
      <PageHeader
        title="Changelog"
        actions={
          <Select
            value={version ?? "all"}
            onValueChange={(selected) =>
              navigate({ search: { version: selected === "all" ? undefined : selected } })
            }
          >
            <SelectTrigger
              size="small"
              aria-label="Release version"
              className="w-36 pointer-coarse:text-sm md:w-44"
            >
              <SelectValue placeholder="All versions" />
            </SelectTrigger>
            <SelectContent position="popper" align="end">
              <SelectItem value="all">All versions</SelectItem>
              {releases.map((entry) => (
                <SelectItem key={entry.version} value={entry.version}>
                  <span className="text-label-13-mono">v{entry.version}</span>
                  {entry.version === VERSION && (
                    <span className="text-gray-900 text-label-12">Installed</span>
                  )}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        }
      />

      {/* The bar Vercel's changelog opens with, carrying the page's one line
          of introduction. */}
      <p className="mb-10 flex min-h-13 items-center gap-2.5 rounded-lg bg-gray-100 px-4 py-3 text-copy-14 text-gray-900 md:mb-12 md:justify-center md:text-center">
        <History className="size-4 shrink-0 text-gray-1000" />
        What's changed in {BRAND_NAME}, newest first.
      </p>

      {visibleReleases.length > 0 ? (
        <ol className="flex flex-col">
          {visibleReleases.map((release) => (
            <li
              key={release.version}
              className="group/release relative grid grid-cols-[24px_minmax(0,1fr)] gap-x-5 pb-12 last:pb-2 md:grid-cols-[9rem_24px_minmax(0,1fr)] md:gap-x-8 md:pb-14"
            >
              {/* The rail: one hairline through every entry, a marker where
                  each begins. It stops at the last marker. */}
              <span
                aria-hidden="true"
                className="absolute top-3 bottom-0 left-[11.5px] w-px bg-gray-alpha-400 group-last/release:hidden md:left-[calc(9rem+2rem+11.5px)]"
              />
              <span
                aria-hidden="true"
                className="relative col-start-1 row-start-1 mt-1.5 flex size-6 items-center justify-center rounded-full bg-gray-1000 text-background-100 md:col-start-2 md:mt-2"
              >
                <Plus className="size-3.5" strokeWidth={2.5} />
              </span>

              {/* Sticky so a long release keeps saying when it shipped, as
                  Vercel's changelog does with its dates. */}
              <div className="col-start-2 row-start-1 flex min-h-9 flex-wrap items-center gap-x-3 gap-y-1 self-start md:sticky md:top-6 md:col-start-1 md:min-h-0 md:flex-col md:items-start md:pt-2.5">
                <time dateTime={release.date} className="text-copy-14 text-gray-900 tabular-nums">
                  {releaseDate.format(new Date(release.date))}
                </time>
                {release.version === VERSION && (
                  <Badge variant="blue-subtle" size="sm">
                    Installed
                  </Badge>
                )}
              </div>

              <article className="col-start-2 row-start-2 grid min-w-0 content-start gap-6 md:col-start-3 md:row-start-1">
                <h2 className="text-gray-1000 text-heading-24 tabular-nums md:text-heading-32">
                  v{release.version}
                </h2>
                {release.sections.map((section) => (
                  <section key={section.title} className="grid gap-3">
                    <h3 className="text-gray-1000 text-heading-16">{section.title}</h3>
                    <ul className="grid list-disc gap-2 pl-5 text-copy-16 text-gray-900 marker:text-gray-700">
                      {section.items.map((item) => (
                        <li key={item} className="pl-1">
                          <NoteText text={item} />
                        </li>
                      ))}
                    </ul>
                  </section>
                ))}
                {release.version === (version ?? VERSION) && (
                  <ViewedRelease key={release.version} version={release.version} />
                )}
              </article>
            </li>
          ))}
        </ol>
      ) : (
        <EmptyState
          icon={<ScrollText />}
          title={
            <>
              No notes for <span className="font-mono">v{version}</span>
            </>
          }
          description="Release notes for this version aren't available in this build."
        >
          <Button
            variant="secondary"
            size="small"
            className="pointer-coarse:h-10"
            onClick={() => navigate({ search: { version: undefined } })}
          >
            Show all versions
          </Button>
        </EmptyState>
      )}
    </div>
  );
}

function ViewedRelease({ version }: { version: string }) {
  const { viewed, markViewed } = useChangelog(version);
  const { mutate, status } = markViewed;
  useEffect(() => {
    if (viewed.data?.viewed === false && status === "idle") mutate({ version });
  }, [viewed.data?.viewed, status, mutate, version]);

  if (!viewed.isError && !markViewed.isError) return null;
  return (
    <Note
      variant="error"
      role="alert"
      action={
        <Button variant="secondary" size="small" onClick={() => mutate({ version })}>
          Try Again
        </Button>
      }
    >
      Couldn't save that you've viewed this release.
    </Note>
  );
}

function NoteText({ text }: { text: string }) {
  return parseInlineNote(text).map((part, index) => {
    const key = `${index}-${part.kind}`;
    if (part.kind === "strong") {
      return (
        <strong key={key} className="font-semibold text-gray-1000">
          {part.text}
        </strong>
      );
    }
    if (part.kind === "code") {
      return (
        <code
          key={key}
          className="rounded-md border border-gray-alpha-400 bg-gray-100 px-1 py-0.5 font-mono text-[0.857em] text-gray-1000"
        >
          {part.text}
        </code>
      );
    }
    if (part.kind === "link") {
      return (
        <a
          key={key}
          href={part.href}
          target="_blank"
          rel="noreferrer"
          className={cn(
            "rounded-sm text-gray-1000 underline decoration-gray-alpha-400 underline-offset-[3px] outline-none transition-colors hover:decoration-gray-1000 focus-visible:shadow-focus-ring",
            // release-please links each note to its commit by short hash.
            /^[0-9a-f]{7,40}$/.test(part.text) && "font-mono text-[0.875em]",
          )}
        >
          {part.text}
        </a>
      );
    }
    return part.text;
  });
}
